/**
 * Application Exécutant iPad - Affiche une seule tâche à la fois
 * Interface full-screen optimisée pour tablette
 */

import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Worksheet,
  WorksheetTask,
  SkipReason,
} from '../../types/worksheets.types';
import {
  getWorksheets,
  getWorksheet,
  startWorksheet,
  startTask,
  completeTask,
  skipTask,
} from '../../services/worksheets.api';
import api from '../../services/api';

// Skip reasons with labels
const SKIP_REASONS: { value: SkipReason; label: string }[] = [
  { value: 'NOT_APPLICABLE', label: 'Non applicable' },
  { value: 'MISSING_MATERIAL', label: 'Matériel manquant' },
  { value: 'ALREADY_DONE', label: 'Déjà fait' },
  { value: 'MANAGER_DECISION', label: 'Décision manager' },
  { value: 'OTHER', label: 'Autre raison' },
];

interface Vendor {
  id: string;
  name: string;
  role: string;
}

export default function ExecutorApp() {
  const [searchParams] = useSearchParams();
  const vendorIdParam = searchParams.get('vendor_id');

  // Auth state
  const [vendor, setVendor] = useState<Vendor | null>(null);
  const [pin, setPin] = useState('');
  const [showPinInput, setShowPinInput] = useState(!vendorIdParam);
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [selectedVendorId, setSelectedVendorId] = useState(vendorIdParam || '');

  // Worksheet state
  const [worksheets, setWorksheets] = useState<Worksheet[]>([]);
  const [currentWorksheet, setCurrentWorksheet] = useState<Worksheet | null>(null);
  const [currentTask, setCurrentTask] = useState<WorksheetTask | null>(null);

  // UI state
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showSkipModal, setShowSkipModal] = useState(false);
  const [skipReason, setSkipReason] = useState<SkipReason>('NOT_APPLICABLE');
  const [skipComment, setSkipComment] = useState('');
  const [resultValue, setResultValue] = useState('');
  const [completing, setCompleting] = useState(false);

  // Load vendors for selection
  useEffect(() => {
    const loadVendors = async () => {
      try {
        const tenantData = localStorage.getItem('tenant');
        if (!tenantData) return;
        const tenant = JSON.parse(tenantData);
        const vendorsList = await api.listVendors(tenant.id);
        setVendors(vendorsList.filter((v: Vendor) => v.role !== 'ADMIN'));
      } catch (err) {
        console.error('Failed to load vendors:', err);
      }
    };
    loadVendors();
  }, []);

  // Authenticate with PIN
  const handlePinSubmit = async () => {
    if (!selectedVendorId || !pin) return;
    try {
      setLoading(true);
      setError(null);
      const result = await api.verifyVendorPin(selectedVendorId, pin);
      if (result.valid) {
        const selectedVendor = vendors.find((v) => v.id === selectedVendorId);
        if (selectedVendor) {
          setVendor(selectedVendor);
          setShowPinInput(false);
          loadAssignedWorksheets(selectedVendorId);
        }
      } else {
        setError('Code PIN incorrect');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  // Load worksheets assigned to vendor
  const loadAssignedWorksheets = useCallback(async (vendorId: string) => {
    try {
      setLoading(true);
      setError(null);
      const allWorksheets = await getWorksheets({
        status: 'TODO' as any, // Get TODO and IN_PROGRESS
      });
      // Filter for assigned to current vendor
      const assigned = allWorksheets.filter(
        (ws) =>
          ws.assigned_to_id === vendorId &&
          (ws.status === 'TODO' || ws.status === 'IN_PROGRESS')
      );
      setWorksheets(assigned);

      // If there's a worksheet in progress, load it
      const inProgress = assigned.find((ws) => ws.status === 'IN_PROGRESS');
      if (inProgress) {
        await loadWorksheet(inProgress.id);
      }
    } catch (err) {
      console.error('Failed to load worksheets:', err);
      setError('Erreur de chargement des worksheets');
    } finally {
      setLoading(false);
    }
  }, []);

  // Load specific worksheet with tasks
  const loadWorksheet = async (worksheetId: string) => {
    try {
      const ws = await getWorksheet(worksheetId);
      setCurrentWorksheet(ws);

      // Find first non-completed task
      if (ws.tasks && ws.tasks.length > 0) {
        const activeTask = ws.tasks.find(
          (t) => t.status === 'TODO' || t.status === 'IN_PROGRESS'
        );
        if (activeTask) {
          setCurrentTask(activeTask);
          // Start task if not already started
          if (activeTask.status === 'TODO') {
            await startTask(worksheetId, activeTask.id);
          }
        } else {
          // All tasks done
          setCurrentTask(null);
        }
      }
    } catch (err) {
      console.error('Failed to load worksheet:', err);
      setError('Erreur de chargement');
    }
  };

  // Start a worksheet
  const handleStartWorksheet = async (worksheetId: string) => {
    try {
      setLoading(true);
      await startWorksheet(worksheetId);
      await loadWorksheet(worksheetId);
    } catch (err) {
      console.error('Failed to start worksheet:', err);
      setError('Erreur au démarrage');
    } finally {
      setLoading(false);
    }
  };

  // Complete current task
  const handleCompleteTask = async () => {
    if (!currentWorksheet || !currentTask) return;

    try {
      setCompleting(true);
      const result = await completeTask(currentWorksheet.id, currentTask.id, {
        result_value: resultValue || undefined,
      });

      setResultValue('');

      if (result.worksheet_completed) {
        // Worksheet is done, go back to list
        setCurrentWorksheet(null);
        setCurrentTask(null);
        if (vendor) {
          await loadAssignedWorksheets(vendor.id);
        }
      } else if (result.next_task) {
        // Move to next task
        setCurrentTask(result.next_task);
        // Start it
        await startTask(currentWorksheet.id, result.next_task.id);
      }
    } catch (err) {
      console.error('Failed to complete task:', err);
      setError('Erreur lors de la validation');
    } finally {
      setCompleting(false);
    }
  };

  // Skip current task
  const handleSkipTask = async () => {
    if (!currentWorksheet || !currentTask) return;

    try {
      setCompleting(true);
      const result = await skipTask(currentWorksheet.id, currentTask.id, {
        reason: skipReason,
        comment: skipComment || undefined,
      });

      setShowSkipModal(false);
      setSkipReason('NOT_APPLICABLE');
      setSkipComment('');

      if (result.worksheet_completed) {
        setCurrentWorksheet(null);
        setCurrentTask(null);
        if (vendor) {
          await loadAssignedWorksheets(vendor.id);
        }
      } else if (result.next_task) {
        setCurrentTask(result.next_task);
        await startTask(currentWorksheet.id, result.next_task.id);
      }
    } catch (err) {
      console.error('Failed to skip task:', err);
      setError('Erreur lors du passage');
    } finally {
      setCompleting(false);
    }
  };

  // Logout
  const handleLogout = () => {
    setVendor(null);
    setShowPinInput(true);
    setPin('');
    setCurrentWorksheet(null);
    setCurrentTask(null);
    setWorksheets([]);
  };

  // PIN Input View
  if (showPinInput) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-600 to-green-800 flex items-center justify-center p-8">
        <div className="bg-white rounded-3xl shadow-2xl p-12 w-full max-w-lg">
          <div className="text-center mb-8">
            <h1 className="text-4xl font-bold text-gray-900 mb-2">Worksheets</h1>
            <p className="text-xl text-gray-500">Interface Exécutant</p>
          </div>

          {/* Vendor selection */}
          <div className="mb-6">
            <label className="block text-lg font-medium text-gray-700 mb-3">
              Sélectionnez votre nom
            </label>
            <select
              value={selectedVendorId}
              onChange={(e) => setSelectedVendorId(e.target.value)}
              className="w-full px-6 py-4 text-xl border-2 border-gray-300 rounded-xl focus:border-green-500 focus:ring-2 focus:ring-green-200"
            >
              <option value="">Choisir...</option>
              {vendors.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.name}
                </option>
              ))}
            </select>
          </div>

          {/* PIN input */}
          <div className="mb-8">
            <label className="block text-lg font-medium text-gray-700 mb-3">
              Code PIN
            </label>
            <input
              type="password"
              value={pin}
              onChange={(e) => setPin(e.target.value)}
              maxLength={4}
              placeholder="****"
              className="w-full px-6 py-4 text-3xl text-center tracking-[1em] border-2 border-gray-300 rounded-xl focus:border-green-500 focus:ring-2 focus:ring-green-200"
            />
          </div>

          {/* Error */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-center">
              {error}
            </div>
          )}

          {/* Submit */}
          <button
            onClick={handlePinSubmit}
            disabled={!selectedVendorId || pin.length < 4 || loading}
            className="w-full py-5 bg-green-600 text-white text-2xl font-semibold rounded-xl hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {loading ? 'Connexion...' : 'Valider'}
          </button>
        </div>
      </div>
    );
  }

  // Loading state
  if (loading && !currentTask) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-green-600 mx-auto mb-4" />
          <p className="text-xl text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  // Worksheet list view
  if (!currentWorksheet) {
    return (
      <div className="min-h-screen bg-gray-100 p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Mes Worksheets</h1>
            <p className="text-xl text-gray-500 mt-1">Bonjour {vendor?.name}</p>
          </div>
          <button
            onClick={handleLogout}
            className="px-6 py-3 bg-gray-200 text-gray-700 rounded-xl hover:bg-gray-300 transition-colors"
          >
            Déconnexion
          </button>
        </div>

        {/* Worksheets grid */}
        {worksheets.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-lg p-12 text-center">
            <svg
              className="w-24 h-24 text-gray-300 mx-auto mb-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
              />
            </svg>
            <h2 className="text-2xl font-semibold text-gray-700 mb-2">
              Aucune worksheet assignée
            </h2>
            <p className="text-lg text-gray-500">
              Vous n'avez pas de tâches en attente pour le moment
            </p>
          </div>
        ) : (
          <div className="grid gap-6 md:grid-cols-2">
            {worksheets.map((ws) => (
              <div
                key={ws.id}
                className="bg-white rounded-2xl shadow-lg p-6 cursor-pointer hover:shadow-xl transition-shadow"
                onClick={() => handleStartWorksheet(ws.id)}
              >
                <div className="flex items-start justify-between mb-4">
                  <h2 className="text-2xl font-bold text-gray-900">{ws.title}</h2>
                  <span
                    className={`px-4 py-2 rounded-full text-sm font-medium ${
                      ws.status === 'IN_PROGRESS'
                        ? 'bg-yellow-100 text-yellow-700'
                        : 'bg-blue-100 text-blue-700'
                    }`}
                  >
                    {ws.status === 'IN_PROGRESS' ? 'En cours' : 'A faire'}
                  </span>
                </div>
                {ws.description && (
                  <p className="text-gray-600 mb-4">{ws.description}</p>
                )}
                <div className="flex items-center gap-4 text-gray-500">
                  <span>{ws.tasks?.length || 0} tâches</span>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Refresh button */}
        <button
          onClick={() => vendor && loadAssignedWorksheets(vendor.id)}
          className="fixed bottom-8 right-8 p-4 bg-green-600 text-white rounded-full shadow-lg hover:bg-green-700 transition-colors"
        >
          <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
            />
          </svg>
        </button>
      </div>
    );
  }

  // Current task view
  if (!currentTask) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-500 to-green-700 flex items-center justify-center p-8">
        <div className="bg-white rounded-3xl shadow-2xl p-12 text-center max-w-lg">
          <svg
            className="w-32 h-32 text-green-500 mx-auto mb-8"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Terminé !</h1>
          <p className="text-xl text-gray-600 mb-8">
            Toutes les tâches de cette worksheet sont complétées
          </p>
          <button
            onClick={() => {
              setCurrentWorksheet(null);
              if (vendor) loadAssignedWorksheets(vendor.id);
            }}
            className="px-8 py-4 bg-green-600 text-white text-xl font-semibold rounded-xl hover:bg-green-700 transition-colors"
          >
            Retour aux worksheets
          </button>
        </div>
      </div>
    );
  }

  // Task progress
  const tasks = currentWorksheet.tasks || [];
  const currentIndex = tasks.findIndex((t) => t.id === currentTask.id);
  const progress = tasks.length > 0 ? ((currentIndex + 1) / tasks.length) * 100 : 0;

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col">
      {/* Header */}
      <div className="bg-white shadow-sm px-8 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-gray-900">
              {currentWorksheet.title}
            </h1>
            <p className="text-gray-500">
              Tâche {currentIndex + 1} / {tasks.length}
            </p>
          </div>
          <button
            onClick={() => {
              setCurrentWorksheet(null);
              setCurrentTask(null);
            }}
            className="p-2 text-gray-500 hover:text-gray-700"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        {/* Progress bar */}
        <div className="mt-4 w-full bg-gray-200 rounded-full h-2">
          <div
            className="bg-green-600 h-2 rounded-full transition-all duration-500"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      {/* Task content */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="bg-white rounded-3xl shadow-xl p-12 w-full max-w-2xl text-center">
          {/* Task number */}
          <div className="inline-flex items-center justify-center w-20 h-20 bg-green-100 rounded-full mb-8">
            <span className="text-3xl font-bold text-green-600">
              {currentIndex + 1}
            </span>
          </div>

          {/* Task title */}
          <h2 className="text-4xl font-bold text-gray-900 mb-6">
            {currentTask.title}
          </h2>

          {/* Instructions */}
          {currentTask.instructions && (
            <p className="text-2xl text-gray-600 mb-8 leading-relaxed">
              {currentTask.instructions}
            </p>
          )}

          {/* Action type specific input */}
          {(currentTask.action_type === 'COUNT' ||
            currentTask.action_type === 'FREE_TEXT' ||
            currentTask.action_type === 'WEIGH') && (
            <div className="mb-8">
              <input
                type={currentTask.action_type === 'COUNT' ? 'number' : 'text'}
                value={resultValue}
                onChange={(e) => setResultValue(e.target.value)}
                placeholder={
                  currentTask.action_type === 'COUNT'
                    ? 'Entrez la quantité...'
                    : currentTask.action_type === 'WEIGH'
                    ? 'Entrez le poids...'
                    : 'Entrez votre réponse...'
                }
                className="w-full px-6 py-5 text-2xl text-center border-2 border-gray-300 rounded-xl focus:border-green-500 focus:ring-2 focus:ring-green-200"
              />
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700">
              {error}
            </div>
          )}
        </div>
      </div>

      {/* Action buttons */}
      <div className="bg-white shadow-lg px-8 py-6 safe-area-inset-bottom">
        <div className="flex gap-4 max-w-2xl mx-auto">
          {/* Skip button */}
          <button
            onClick={() => setShowSkipModal(true)}
            disabled={completing}
            className="flex-1 py-5 bg-gray-200 text-gray-700 text-xl font-semibold rounded-xl hover:bg-gray-300 disabled:opacity-50 transition-colors"
          >
            Passer
          </button>

          {/* Complete button */}
          <button
            onClick={handleCompleteTask}
            disabled={completing}
            className="flex-[2] py-5 bg-green-600 text-white text-xl font-semibold rounded-xl hover:bg-green-700 disabled:opacity-50 transition-colors flex items-center justify-center gap-3"
          >
            {completing ? (
              <>
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white" />
                En cours...
              </>
            ) : (
              <>
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                Fait
              </>
            )}
          </button>
        </div>
      </div>

      {/* Skip modal */}
      {showSkipModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-8 z-50">
          <div className="bg-white rounded-3xl shadow-2xl p-8 w-full max-w-lg">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              Passer cette tâche
            </h2>

            {/* Reason selection */}
            <div className="space-y-3 mb-6">
              {SKIP_REASONS.map((reason) => (
                <label
                  key={reason.value}
                  className={`flex items-center p-4 border-2 rounded-xl cursor-pointer transition-colors ${
                    skipReason === reason.value
                      ? 'border-orange-500 bg-orange-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <input
                    type="radio"
                    name="skipReason"
                    value={reason.value}
                    checked={skipReason === reason.value}
                    onChange={() => setSkipReason(reason.value)}
                    className="w-5 h-5 text-orange-600"
                  />
                  <span className="ml-3 text-lg">{reason.label}</span>
                </label>
              ))}
            </div>

            {/* Comment for OTHER */}
            {skipReason === 'OTHER' && (
              <div className="mb-6">
                <textarea
                  value={skipComment}
                  onChange={(e) => setSkipComment(e.target.value)}
                  placeholder="Précisez la raison..."
                  rows={3}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-orange-500 focus:ring-2 focus:ring-orange-200"
                />
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-4">
              <button
                onClick={() => {
                  setShowSkipModal(false);
                  setSkipReason('NOT_APPLICABLE');
                  setSkipComment('');
                }}
                className="flex-1 py-4 bg-gray-200 text-gray-700 font-semibold rounded-xl hover:bg-gray-300 transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={handleSkipTask}
                disabled={completing || (skipReason === 'OTHER' && !skipComment.trim())}
                className="flex-1 py-4 bg-orange-500 text-white font-semibold rounded-xl hover:bg-orange-600 disabled:opacity-50 transition-colors"
              >
                {completing ? 'En cours...' : 'Confirmer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
